import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Button } from "../../../components/ui/Button/Button";
import {
  dateLabel,
  defaultRange,
  previousRange,
  shiftDate,
  validDate,
} from "../dateRange";
import type { DateRange } from "../types";
import styles from "../Analytics.module.css";
export default function DateRangeControl({
  range,
  onChange,
}: {
  range: DateRange;
  onChange: (range: DateRange) => void;
}) {
  const [start, setStart] = useState(range.start),
    [end, setEnd] = useState(range.end);
  useEffect(() => {
    setStart(range.start);
    setEnd(range.end);
  }, [range.start, range.end]);
  const valid =
    validDate(start) &&
    validDate(end) &&
    start <= end &&
    Date.parse(end) - Date.parse(start) <= 365 * 86400000;
  const previous = previousRange(range);
  return (
    <div className={styles.dateArea}>
      <form
        className={styles.dateForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onChange({ ...range, start, end });
        }}
      >
        <CalendarDays size={17} aria-hidden="true" />
        <label>
          From
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            required
          />
        </label>
        <span aria-hidden="true">—</span>
        <label>
          To
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            required
          />
        </label>
        <Button variant="secondary" type="submit" disabled={!valid}>
          Apply
        </Button>
        <label className={styles.srOnly} htmlFor="analytics-preset">
          Date preset
        </label>
        <select
          id="analytics-preset"
          value=""
          onChange={(e) => {
            if (e.target.value) {
              const days = Number(e.target.value);
              const last = defaultRange().end;
              onChange({
                ...range,
                start: shiftDate(last, 1 - days),
                end: last,
              });
            }
          }}
        >
          <option value="" disabled>
            Quick range
          </option>
          <option value="7">Last 7 days</option>
          <option value="28">Last 28 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </form>
      {!valid && (
        <p role="alert">
          Choose a valid period of up to 366 days, with the start on or before
          the end.
        </p>
      )}
      <div className={styles.compareRow}>
        <label>
          <input
            type="checkbox"
            checked={range.compare === "previous"}
            onChange={(e) =>
              onChange({
                ...range,
                compare: e.target.checked ? "previous" : "none",
              })
            }
          />
          Compare with previous period
        </label>
        {range.compare === "previous" && (
          <span>
            {dateLabel(previous.start)} – {dateLabel(previous.end)}
          </span>
        )}
      </div>
    </div>
  );
}
